import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { _postQrToken } from '@/api/user/mutations';
import { useGetUserProfile } from '@/api/user-dashboard/queries';

import { personalQrUrl } from './personal-qr-value';

export type UsePersonalQrResult = {
  qrValue: string | null;
  // True whenever the live mint is unavailable and the value shown is the
  // ref-only fallback — not only when the browser is actually offline (a
  // 500, a 401, or the endpoint's own rate limit fall back the same way).
  // Named for what callers need to know (is this the fallback value?), not
  // for the one cause among several that can produce it.
  isFallback: boolean;
};

// Re-mint at 85% of the token's lifetime rather than at expiry. Re-minting
// exactly on the deadline leaves a window — network latency plus the round
// trip to fetch the replacement — during which the QR on screen still shows
// the old, now-expired token, and the scanner rejects it as unknown.
const REMINT_LIFETIME_FRACTION = 0.85;

// Once the displayed mint has gone stale the scheduled re-mint has already
// fired and failed, so nothing is left to bring the live token back on its
// own. Retry on a fixed cadence instead: short enough to recover while someone
// is standing at a door, long enough not to hammer the endpoint's rate limit.
const STALE_MINT_RETRY_INTERVAL_MS = 15_000;

/**
 * Mints the personal QR token and re-mints it before it expires. The re-mint
 * timer is derived from `expiresInSeconds`, never `expiresAt` — trusting the
 * server's absolute timestamp against the client's own clock would add clock
 * skew on top of network latency (see qr-code-modal.tsx's invite countdown,
 * which follows the same rule).
 *
 * If minting fails — no network, backend unreachable, a server error —
 * falls back to a ref-only value built from the cached profile's `qrCode`.
 * Event check-in reads only `ref`, so the fallback still gets someone into
 * the event; it just can't be used to connect (that needs a live signature).
 */
export const usePersonalQr = (enabled: boolean): UsePersonalQrResult => {
  const { user } = useGetUserProfile();
  const [isMintStale, setIsMintStale] = useState(false);

  const {
    data: qrTokenData,
    dataUpdatedAt,
    isError,
    fetchStatus,
    refetch,
  } = useQuery({
    queryKey: ['qr-token-generate'],
    queryFn: _postQrToken,
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    // TanStack Query v5 defaults to networkMode: 'online', which *pauses* a
    // query while the browser is offline instead of attempting it —
    // fetchStatus stays 'paused', status stays 'pending', and isError never
    // becomes true, so the offline fallback below would never activate in
    // the exact situation it exists for. 'always' makes the attempt fire and
    // fail fast, the same way it would against an unreachable backend.
    networkMode: 'always',
    // No retries: with no signal, the default 3-attempt exponential backoff
    // just delays the offline fallback by seconds someone is standing at a
    // door for. One failed attempt is enough to fall back; the query
    // client's default refetchOnReconnect picks the real mint back up as
    // soon as the network returns.
    retry: false,
  });

  const token = qrTokenData?.token;
  const expiresInSeconds = qrTokenData?.expiresInSeconds;

  useEffect(() => {
    if (!enabled || !token || !expiresInSeconds) return;

    setIsMintStale(false);
    const remintTimeout = setTimeout(refetch, expiresInSeconds * REMINT_LIFETIME_FRACTION * 1000);
    // A failed re-mint leaves React Query holding the last successful data, so the query
    // itself never stops reporting the old token — `isError` sits alongside live-looking
    // `data`. Time the mint out locally instead, or the UI keeps presenting an expired
    // signature as live and every connect scan fails with nothing on screen saying why.
    const staleTimeout = setTimeout(() => setIsMintStale(true), expiresInSeconds * 1000);

    return () => {
      clearTimeout(remintTimeout);
      clearTimeout(staleTimeout);
    };
    // `dataUpdatedAt` restarts both timers on every successful mint, including one that
    // happens to return an identical token; a failed re-mint does not move it.
  }, [enabled, token, expiresInSeconds, dataUpdatedAt, refetch]);

  useEffect(() => {
    if (!enabled || !isMintStale) return;

    const retryInterval = setInterval(refetch, STALE_MINT_RETRY_INTERVAL_MS);

    return () => clearInterval(retryInterval);
  }, [enabled, isMintStale, refetch]);

  if (token && !isMintStale) {
    return { qrValue: personalQrUrl(location.origin, token), isFallback: false };
  }

  // A stale token still carries its `ref`, and check-in reads only that, so it beats
  // showing nothing when the profile holds no ref of its own.
  const fallbackRef = user?.qrCode ?? user?.id ?? token ?? null;
  // fetchStatus === 'paused' belt-and-suspenders alongside isError: with
  // networkMode 'always' a paused fetch should not normally occur, but
  // falling back on it too costs nothing and covers any query-client
  // configuration that pauses fetches for reasons other than this query's
  // own networkMode.
  if ((isError || isMintStale || fetchStatus === 'paused') && fallbackRef) {
    return { qrValue: personalQrUrl(location.origin, fallbackRef), isFallback: true };
  }

  return { qrValue: null, isFallback: false };
};
